/* Lavender Farm Simulator — living lavender field backdrop.
   Replaces the header's flat sky/flower strip with a painted-style perspective lavender field that follows
   the farm: time of day (sun, moon, stars, dawn/dusk light), season (dormant, green-up, bud, bloom, harvest,
   autumn, snow) and the farm-day weather (clear, clouds, drizzle/rain, snow, fog, wind, frost mornings).
   Reads the game state only; never changes it. Honors reduced motion and pauses when off screen. */
(function () {
  'use strict';
  if (window.__gllfSimScene) return;
  window.__gllfSimScene = true;

  var root, A, sky, cvs, ctx, fx, fctx, W = 0, H = 0, DPR = 1, key = '', env = null, visible = true, raf = 0, parts = [];
  var override = null;
  var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- helpers ---------- */
  function rnd(seed) { var s = seed % 2147483647; if (s <= 0) s += 2147483646; return function () { s = s * 16807 % 2147483647; return (s - 1) / 2147483646; }; }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function mixC(a, b, t) { return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)]; }
  function css(c, a) { return 'rgba(' + Math.round(c[0]) + ',' + Math.round(c[1]) + ',' + Math.round(c[2]) + ',' + (a == null ? 1 : a) + ')'; }
  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

  /* ---------- read the farm ---------- */
  function readEnv() {
    var st = {}; try { st = A.state() || {}; } catch (e) {}
    var t = st.time || {}, f = t.forecast || [], d = f[Math.max(0, Math.min(f.length - 1, +t.day || 0))] || {};
    var date = new Date(String(d.date || '') + 'T12:00:00Z');
    if (isNaN(date)) date = new Date();
    var month = date.getUTCMonth(), dom = date.getUTCDate(), doy = Math.floor((Date.UTC(date.getUTCFullYear(), month, dom) - Date.UTC(date.getUTCFullYear(), 0, 1)) / 864e5);
    var minute = (+t.minute || 0) % 1440;
    var cond = String(d.condition || '').toLowerCase();
    var rain = +d.rain || +d.precip || 0, snow = +d.snowfall || 0, wind = +d.wind || 0, low = +d.low, high = +d.high;
    var kind = /thunder|storm/.test(cond) ? 'storm' : /snow|flurr|sleet/.test(cond) || snow > 0 ? 'snow' : /rain|shower/.test(cond) ? 'rain' : /drizzle/.test(cond) ? 'drizzle' : /fog|mist|haze/.test(cond) ? 'fog' : /overcast/.test(cond) ? 'overcast' : /cloud/.test(cond) ? 'cloudy' : 'clear';
    /* daylight for about 43°N: sunrise/sunset swing through the year */
    var yr = Math.cos((doy - 172) / 365 * 2 * Math.PI);          /* 1 at summer solstice, -1 at winter */
    var rise = lerp(7.9, 6.0, (yr + 1) / 2) * 60, set = lerp(17.1, 21.3, (yr + 1) / 2) * 60;
    /* lavender phenology for a Michigan field, by day of year */
    var stage;
    if (doy < 95 || doy >= 335) stage = 'dormant';
    else if (doy < 135) stage = 'greenup';
    else if (doy < 162) stage = 'leafy';
    else if (doy < 176) stage = 'bud';
    else if (doy < 212) stage = 'bloom';
    else if (doy < 240) stage = 'harvested';
    else if (doy < 300) stage = 'autumn';
    else stage = 'late';
    var snowCover = (snow > 0 || (kind === 'snow')) || ((doy < 70 || doy > 345) && low <= 28);
    var frost = isFinite(low) && low <= 32 && minute < 10 * 60 && !snowCover;
    var out = { minute: minute, rise: rise, set: set, month: month, doy: doy, kind: kind, rain: rain, wind: wind, stage: stage, snowCover: snowCover, frost: frost, low: low, high: high };
    if (override) Object.keys(override).forEach(function (k) { out[k] = override[k]; });
    return out;
  }

  /* ---------- sky & light ---------- */
  function light(e) {
    var m = e.minute, dawn = 45;
    var day = clamp(Math.min((m - (e.rise - dawn)) / (dawn * 2), (e.set + dawn - m) / (dawn * 2)), 0, 1);  /* 0 night .. 1 day */
    var golden = clamp(1 - Math.abs(Math.min(Math.abs(m - e.rise), Math.abs(m - e.set)) - 20) / 70, 0, 1) * (day > 0 && day < 1 ? 1 : .35);
    var cloud = { clear: 0, cloudy: .35, overcast: .7, fog: .6, drizzle: .7, rain: .8, storm: .95, snow: .75 }[e.kind] || 0;
    return { day: day, golden: e.kind === 'clear' || e.kind === 'cloudy' ? golden : golden * .3, cloud: cloud };
  }
  function drawSky(e, L) {
    var nightTop = [14, 18, 44], nightBot = [42, 38, 78];
    var dayTop = [96, 160, 214], dayBot = [196, 224, 240];
    var greyTop = [132, 142, 156], greyBot = [186, 190, 196];
    var top = mixC(nightTop, mixC(dayTop, greyTop, L.cloud), L.day), bot = mixC(nightBot, mixC(dayBot, greyBot, L.cloud), L.day);
    if (L.golden > 0) { bot = mixC(bot, [255, 170, 120], L.golden * .75); top = mixC(top, [120, 110, 170], L.golden * .35); }
    var hy = H * .5, g = ctx.createLinearGradient(0, 0, 0, hy);
    g.addColorStop(0, css(top)); g.addColorStop(1, css(bot));
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, hy + 2);
    /* stars */
    if (L.day < .35 && L.cloud < .8) {
      var r = rnd(99), a = (1 - L.day / .35) * (1 - L.cloud);
      for (var i = 0; i < 140; i++) { ctx.fillStyle = 'rgba(255,255,255,' + (a * (.3 + r() * .7)).toFixed(2) + ')'; ctx.fillRect(r() * W, r() * hy * .9, r() < .1 ? 2 : 1, r() < .1 ? 2 : 1); }
    }
    /* sun or moon along an arc */
    var isDay = e.minute > e.rise && e.minute < e.set;
    var p = isDay ? (e.minute - e.rise) / (e.set - e.rise) : ((e.minute - e.set + 1440) % 1440) / (1440 - (e.set - e.rise));
    var x = lerp(W * .08, W * .92, p), y = hy - Math.sin(p * Math.PI) * hy * .8 + 6;
    var body = isDay ? [255, 236, 170] : [236, 238, 250], rad = isDay ? 26 : 18, vis = 1 - L.cloud * (isDay ? .75 : .9);
    var glow = ctx.createRadialGradient(x, y, 0, x, y, rad * 4);
    glow.addColorStop(0, css(body, .55 * vis)); glow.addColorStop(1, css(body, 0));
    ctx.fillStyle = glow; ctx.fillRect(x - rad * 4, y - rad * 4, rad * 8, rad * 8);
    ctx.fillStyle = css(body, vis); ctx.beginPath(); ctx.arc(x, y, rad, 0, Math.PI * 2); ctx.fill();
    if (!isDay) { ctx.fillStyle = css(mixC(top, bot, .5), vis); ctx.beginPath(); ctx.arc(x + 8, y - 5, rad * .9, 0, Math.PI * 2); ctx.fill(); }
    /* cloud banks */
    if (L.cloud > 0) {
      var cr = rnd(7), cc = mixC([90, 96, 116], [240, 242, 246], L.day * (1 - L.cloud * .45));
      for (var k = 0; k < 7 + L.cloud * 12; k++) {
        var cx = cr() * W * 1.2 - W * .1, cy = cr() * hy * .75 + 10, cw = 90 + cr() * 180;
        ctx.fillStyle = css(cc, .35 + L.cloud * .5);
        for (var j = 0; j < 5; j++) { ctx.beginPath(); ctx.ellipse(cx + j * cw / 5, cy + Math.sin(j) * 6, cw / 4, 14 + cr() * 12, 0, 0, Math.PI * 2); ctx.fill(); }
      }
    }
  }

  /* ---------- horizon: tree line, barn ---------- */
  function drawHorizon(e, L) {
    var hy = H * .5, far = mixC([40, 50, 70], mixC([120, 150, 150], [150, 156, 160], L.cloud), L.day);
    var r = rnd(5);
    /* far tree line: soft rounded canopies */
    ctx.fillStyle = css(far);
    ctx.beginPath(); ctx.moveTo(0, hy + 4);
    for (var x = -10; x <= W + 30; x += 16 + r() * 10) { var cr = 8 + r() * 12; ctx.arc(x, hy - cr * .4 - Math.sin(x / 140) * 4, cr, Math.PI, 0); }
    ctx.lineTo(W, hy + 4); ctx.closePath(); ctx.fill();
    var near = mixC([22, 34, 34], mixC([68, 104, 70], [96, 110, 100], L.cloud), L.day);
    if (e.stage === 'autumn' || e.stage === 'late') near = mixC(near, [150, 96, 50], .35 * L.day);
    if (e.snowCover) near = mixC(near, [200, 206, 214], .35 * L.day);
    ctx.fillStyle = css(near);
    ctx.beginPath(); ctx.moveTo(0, hy + 6);
    for (var x2 = -10; x2 <= W + 30; x2 += 10 + r() * 8) { var c2 = 4 + r() * 7; ctx.arc(x2, hy + 1 - c2 * .3, c2, Math.PI, 0); }
    ctx.lineTo(W, hy + 6); ctx.closePath(); ctx.fill();
    /* small red barn and farmhouse far away */
    var bx = W * .72, by = hy + 2, s = 1;
    ctx.fillStyle = css(mixC([60, 20, 24], [160, 50, 45], L.day)); ctx.fillRect(bx, by - 16 * s, 26 * s, 16 * s);
    ctx.fillStyle = css(mixC([40, 30, 34], [96, 80, 80], L.day)); ctx.beginPath(); ctx.moveTo(bx - 3, by - 16); ctx.lineTo(bx + 13, by - 26); ctx.lineTo(bx + 29, by - 16); ctx.fill();
    ctx.fillStyle = css(mixC([60, 58, 64], [236, 230, 216], L.day)); ctx.fillRect(bx + 36, by - 12, 18, 12);
    ctx.fillStyle = css(mixC([40, 30, 34], [120, 70, 76], L.day)); ctx.beginPath(); ctx.moveTo(bx + 34, by - 12); ctx.lineTo(bx + 45, by - 19); ctx.lineTo(bx + 56, by - 12); ctx.fill();
    if (L.day < .4) { ctx.fillStyle = 'rgba(255,214,120,.9)'; ctx.fillRect(bx + 40, by - 8, 3, 3); ctx.fillRect(bx + 47, by - 8, 3, 3); }
  }

  /* ---------- the lavender field ---------- */
  var FOLIAGE = { dormant: [120, 132, 118], greenup: [118, 146, 106], leafy: [104, 140, 96], bud: [98, 134, 92], bloom: [92, 124, 90], harvested: [112, 138, 104], autumn: [138, 150, 128], late: [126, 134, 120] };
  var BLOOM = { dormant: 0, greenup: 0, leafy: 0, bud: .35, bloom: 1, harvested: .08, autumn: 0, late: 0 };
  function drawField(e, L) {
    var hy = H * .5, vx = W * .5, rows = 19, r = rnd(e.doy + 11);
    var wet = e.kind === 'rain' || e.kind === 'storm' || e.kind === 'drizzle';
    var soil = e.snowCover ? [232, 236, 244] : wet ? [70, 54, 42] : [118, 90, 64];
    var dark = 1 - L.day;
    function shade(c, amt) { var x = mixC(c, [18, 20, 40], dark * .8); if (L.golden) x = mixC(x, [255, 176, 110], L.golden * .22); return mixC(x, [150, 156, 166], L.cloud * .16 * L.day); }
    /* ground between rows */
    var g = ctx.createLinearGradient(0, hy, 0, H);
    g.addColorStop(0, css(shade(mixC(soil, [110, 120, 110], .35))));
    g.addColorStop(1, css(shade(mixC(soil, [40, 30, 24], .25))));
    ctx.fillStyle = g; ctx.fillRect(0, hy, W, H - hy);
    var leaf = FOLIAGE[e.stage] || FOLIAGE.leafy, bloom = BLOOM[e.stage] || 0;
    var flowerA = e.stage === 'bud' ? [110, 112, 140] : [120, 86, 186], flowerB = e.stage === 'bud' ? [132, 150, 120] : [156, 124, 214];
    var sizeK = { dormant: .72, greenup: .62, leafy: .86, bud: .95, bloom: 1, harvested: .7, autumn: .9, late: .8 }[e.stage] || .9;
    /* every plant, far to near, so closer plants overlap */
    var items = [];
    for (var i = 0; i < rows; i++) {
      var off = (i - (rows - 1) / 2) / ((rows - 1) / 2);
      for (var t = .015; t < 1.12;) { items.push([t, off, r()]); t += .004 + t * .028; }
    }
    items.sort(function (a, b) { return a[0] - b[0]; });
    items.forEach(function (it) {
      var t = it[0], off = it[1], jit = it[2];
      var y = hy + Math.pow(t, 1.5) * (H - hy) * 1.05;
      var spread = W * 2.1 * t, laneW = spread / (rows - 1);
      var x = vx + off * spread / 2 + (jit - .5) * laneW * .06;
      var rad = laneW * .34 * sizeK * (.92 + jit * .16);
      if (x < -rad * 2 || x > W + rad * 2 || rad < .6) return;
      var rh = rad * .7;
      /* foliage mound */
      var base = shade(mixC(leaf, [200, 210, 200], jit * .12));
      ctx.fillStyle = css(mixC(base, [10, 14, 10], .35));
      ctx.beginPath(); ctx.ellipse(x, y, rad * 1.02, rh * .32, 0, 0, Math.PI * 2); ctx.fill();
      var mg = ctx.createLinearGradient(0, y - rh, 0, y);
      mg.addColorStop(0, css(mixC(base, [235, 240, 225], .22))); mg.addColorStop(1, css(mixC(base, [20, 26, 20], .3)));
      ctx.fillStyle = mg; ctx.beginPath(); ctx.ellipse(x, y - rh * .15, rad, rh * .85, 0, Math.PI, 0); ctx.closePath(); ctx.fill();
      /* fine needle texture on nearer plants */
      if (rad > 9) {
        var rs = rnd(Math.round(x * 7 + y * 3) + 1), n = Math.min(40, Math.round(rad * .9));
        ctx.lineWidth = Math.max(.6, rad * .03);
        for (var k = 0; k < n; k++) {
          var a = Math.PI * (1.08 + rs() * .84), px = x + Math.cos(a) * rad * .92 * rs(), py = y - rh * .15 + Math.sin(a) * rh * .8 * rs();
          ctx.strokeStyle = css(mixC(base, rs() > .5 ? [235, 240, 225] : [30, 40, 30], .25), .7);
          ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px + Math.cos(a) * rad * .14, py + Math.sin(a) * rad * .14); ctx.stroke();
        }
      }
      if (e.snowCover) { ctx.fillStyle = css(shade([246, 248, 252]), .95); ctx.beginPath(); ctx.ellipse(x, y - rh * .62, rad * .82, rh * .42, 0, Math.PI, 0); ctx.closePath(); ctx.fill(); }
      if (e.frost) { ctx.fillStyle = 'rgba(232,242,255,.5)'; ctx.beginPath(); ctx.ellipse(x, y - rh * .6, rad * .78, rh * .35, 0, Math.PI, 0); ctx.closePath(); ctx.fill(); }
      /* flower cap: short upright spikes crowning the mound */
      if (bloom > 0) {
        var capH = rh * (e.stage === 'bud' ? .45 : .9) * (e.stage === 'harvested' ? .3 : 1);
        if (rad <= 5) {
          ctx.fillStyle = css(shade(mixC(flowerA, flowerB, jit)), bloom);
          ctx.beginPath(); ctx.ellipse(x, y - rh * .7, rad * .95, capH * .5, 0, Math.PI, 0); ctx.closePath(); ctx.fill();
        } else {
          var rf = rnd(Math.round(x * 13 + y * 5) + 3), m = Math.round(Math.min(70, rad * 2.2) * bloom);
          for (var q = 0; q < m; q++) {
            var u = rf() * 2 - 1, sx = x + u * rad * .9, sy = y - rh * .15 - Math.sqrt(1 - u * u) * rh * .78;
            var len = capH * (.55 + rf() * .6);
            var lean = u * rad * .12;
            if (rad > 14) { ctx.strokeStyle = css(shade([108, 128, 92]), .9); ctx.lineWidth = Math.max(.6, rad * .02); ctx.beginPath(); ctx.moveTo(sx, sy + len * .2); ctx.lineTo(sx + lean, sy - len * .55); ctx.stroke(); }
            ctx.fillStyle = css(shade(mixC(flowerA, flowerB, rf())), .95);
            ctx.beginPath(); ctx.ellipse(sx + lean, sy - len * .75, Math.max(.8, rad * .045), Math.max(1.4, len * .3), lean / (rad || 1), 0, Math.PI * 2); ctx.fill();
          }
        }
      }
      if (e.stage === 'harvested' && jit > .96 && rad > 10) { ctx.fillStyle = css(shade([124, 92, 184])); ctx.beginPath(); ctx.ellipse(x + rad * .9, y + 2, rad * .35, rad * .08, -.2, 0, Math.PI * 2); ctx.fill(); }
    });
    /* atmospheric haze at the horizon */
    var hz = ctx.createLinearGradient(0, hy - 10, 0, hy + (H - hy) * .3);
    var hc = mixC(mixC([206, 218, 232], [160, 160, 176], L.cloud), [30, 30, 60], dark);
    if (L.golden) hc = mixC(hc, [255, 196, 150], L.golden * .5);
    hz.addColorStop(0, css(hc, .6 + (e.kind === 'fog' ? .3 : 0))); hz.addColorStop(1, css(hc, 0));
    ctx.fillStyle = hz; ctx.fillRect(0, hy - 10, W, (H - hy) * .35);
    if (e.kind === 'fog') { ctx.fillStyle = css(mixC([220, 224, 230], [60, 64, 80], dark), .38); ctx.fillRect(0, 0, W, H); }
    /* soft vignette so the title card stays readable */
    var vg = ctx.createLinearGradient(0, 0, W * .6, 0);
    vg.addColorStop(0, 'rgba(0,0,0,' + (.05 + dark * .12) + ')'); vg.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = vg; ctx.fillRect(0, 0, W * .6, H);
  }

  /* ---------- animated weather & life ---------- */
  function seedParts(e, L) {
    parts = [];
    var n = 0, type = '';
    if (e.kind === 'rain' || e.kind === 'storm') { type = 'rain'; n = e.kind === 'storm' ? 260 : 170; }
    else if (e.kind === 'drizzle') { type = 'rain'; n = 80; }
    else if (e.kind === 'snow') { type = 'snow'; n = 140; }
    else if (L.day < .3 && (e.stage === 'bloom' || e.stage === 'bud' || e.stage === 'leafy') && e.kind !== 'fog') { type = 'firefly'; n = 26; }
    else if (L.day > .6 && e.stage === 'bloom' && L.cloud < .5) { type = 'bee'; n = 18; }
    for (var i = 0; i < n; i++) parts.push({ t: type, x: Math.random() * W, y: Math.random() * H, v: .5 + Math.random(), p: Math.random() * 6.28 });
  }
  function animate() {
    raf = 0;
    if (!visible || document.hidden || !parts.length) return;
    fctx.clearRect(0, 0, W, H);
    var wind = clamp((env && env.wind || 0) / 25, 0, 1), hy = H * .5;
    parts.forEach(function (p) {
      if (p.t === 'rain') {
        p.x += (1.5 + wind * 4) * p.v; p.y += 9 * p.v;
        if (p.y > H) { p.y = -10; p.x = Math.random() * W; } if (p.x > W) p.x -= W;
        fctx.strokeStyle = 'rgba(210,225,245,' + (.28 + p.v * .2) + ')'; fctx.lineWidth = 1;
        fctx.beginPath(); fctx.moveTo(p.x, p.y); fctx.lineTo(p.x - (1.5 + wind * 4) * 1.4, p.y - 12 * p.v); fctx.stroke();
      } else if (p.t === 'snow') {
        p.p += .02; p.x += Math.sin(p.p) * .6 + wind * 1.5; p.y += .8 * p.v;
        if (p.y > H) { p.y = -4; p.x = Math.random() * W; } if (p.x > W) p.x -= W;
        fctx.fillStyle = 'rgba(255,255,255,.85)'; fctx.beginPath(); fctx.arc(p.x, p.y, 1 + p.v * 1.4, 0, 6.28); fctx.fill();
      } else if (p.t === 'firefly') {
        p.p += .03; p.x += Math.cos(p.p * .7) * .4; p.y = Math.max(hy + 10, Math.min(H - 6, p.y + Math.sin(p.p) * .3));
        var a = (Math.sin(p.p * 2.3) + 1) / 2;
        var g = fctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 6); g.addColorStop(0, 'rgba(255,240,140,' + a + ')'); g.addColorStop(1, 'rgba(255,240,140,0)');
        fctx.fillStyle = g; fctx.fillRect(p.x - 6, p.y - 6, 12, 12);
      } else if (p.t === 'bee') {
        p.p += .08; p.x += Math.cos(p.p) * 1.4 + .3; p.y = Math.max(hy + 30, Math.min(H - 10, p.y + Math.sin(p.p * 1.3) * 1.1));
        if (p.x > W + 5) p.x = -5;
        fctx.fillStyle = '#f2b84b'; fctx.beginPath(); fctx.ellipse(p.x, p.y, 2.4, 1.6, 0, 0, 6.28); fctx.fill();
        fctx.fillStyle = 'rgba(255,255,255,.7)'; fctx.beginPath(); fctx.ellipse(p.x - .5, p.y - 2, 1.4, 1, 0, 0, 6.28); fctx.fill();
      }
    });
    if (env && env.kind === 'storm' && Math.random() < .004) { fctx.fillStyle = 'rgba(255,255,255,.55)'; fctx.fillRect(0, 0, W, H); }
    raf = requestAnimationFrame(animate);
  }

  /* ---------- lifecycle ---------- */
  function size() {
    var r = sky.getBoundingClientRect();
    var w = Math.round(r.width), h = Math.round(r.height);
    if (!w || !h) return false;
    DPR = Math.min(2, window.devicePixelRatio || 1);
    if (w === W && h === H && cvs.width === Math.round(w * DPR)) return false;
    W = w; H = h;
    [cvs, fx].forEach(function (c) { c.width = Math.round(W * DPR); c.height = Math.round(H * DPR); });
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0); fctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    return true;
  }
  function render(force) {
    var e = readEnv(), resized = size();
    var k = [Math.floor(e.minute / 5), e.doy, e.kind, e.snowCover, e.frost, W, H].join('|');
    if (!force && !resized && k === key) return;
    key = k; env = e;
    var L = light(e);
    ctx.clearRect(0, 0, W, H);
    drawSky(e, L); drawHorizon(e, L); drawField(e, L);
    sky.setAttribute('data-gllf-scene', e.stage + ' ' + e.kind + (L.day < .3 ? ' night' : ''));
    cvs.setAttribute('aria-label', 'Lavender field: ' + e.stage + ', ' + e.kind);
    seedParts(e, L);
    if (!parts.length) fctx.clearRect(0, 0, W, H);
    if (reduce) { fctx.clearRect(0, 0, W, H); return; }
    if (!raf) raf = requestAnimationFrame(animate);
  }
  function start(r) {
    root = r; A = r._lfg; sky = r.querySelector(':scope > .lfg-sky'); if (!sky) return;
    cvs = document.createElement('canvas'); cvs.className = 'gllf-scene'; cvs.setAttribute('role', 'img');
    fx = document.createElement('canvas'); fx.className = 'gllf-scene gllf-scene--fx'; fx.setAttribute('aria-hidden', 'true');
    ctx = cvs.getContext('2d'); fctx = fx.getContext('2d');
    sky.appendChild(cvs); sky.appendChild(fx); sky.classList.add('gllf-scene-on');
    render(true);
    setInterval(function () { if (!sky.isConnected) return; render(false); }, 2000);
    window.addEventListener('resize', function () { render(true); });
    if ('IntersectionObserver' in window) new IntersectionObserver(function (en) { visible = en[0].isIntersecting; if (visible && !raf && !reduce) raf = requestAnimationFrame(animate); }).observe(sky);
    document.addEventListener('visibilitychange', function () { if (!document.hidden && !raf && !reduce) raf = requestAnimationFrame(animate); });
  }
  /* Design preview only (console): GLLFSimScene.preview({ stage: 'bloom', minute: 1230, kind: 'clear' }); preview(null) resets. */
  window.GLLFSimScene = { preview: function (o) { override = o || null; if (sky) render(true); } };
  var tries = 0;
  (function wait() {
    var r = document.getElementById('lfg-simulator-root');
    if (!r && document.readyState === 'complete') return;
    if (r && r._lfg && typeof r._lfg.state === 'function' && r.querySelector(':scope > .lfg-sky')) return start(r);
    if (++tries < 240) setTimeout(wait, 250);
  })();
})();
